import React from 'react';
import { Link } from 'react-router-dom';
import { Trees as Tree, Truck, Shovel, Phone, Leaf, ArrowRight } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTree } from '@fortawesome/free-solid-svg-icons';
import { useLanguageStore } from '../store';
import { translations } from '../translations';

export default function HomePage() {
  const language = useLanguageStore((state) => state.language);
  const t = translations[language];

  return (
    <div className="flex flex-col">
      {/* Hero Section */}
      <div className="relative min-h-screen">
        {/* Overlay gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-green-900/70 via-green-800/60 to-green-950/90 z-10" />
        
        {/* Background image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url("https://images.unsplash.com/photo-1542273917363-3b1817f69a2d?ixlib=rb-1.2.1&auto=format&fit=crop&w=2000&q=80")'
          }}
        />

        {/* Content */}
        <div className="relative z-20 max-w-7xl mx-auto px-4 h-screen flex items-center">
          <div className="max-w-3xl">
            <div className="inline-flex items-center bg-green-900/40 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
              <Leaf className="w-5 h-5 text-green-400" />
              <span className="text-green-100">{t.home.hero.badge}</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold mb-6 text-white leading-tight">
              {t.home.hero.title.line1} <br />
              <span className="text-green-400">{t.home.hero.title.line2}</span>
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-gray-200 max-w-2xl">
              {t.home.hero.subtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/katalog"
                className="inline-flex items-center justify-center bg-green-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-green-700 transition-colors group"
              >
                {t.home.hero.catalogButton}
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
              <a
                href="tel:696022651"
                className="inline-flex items-center justify-center bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white/20 transition-colors"
              >
                <Phone className="w-5 h-5 mr-2" />
                {t.home.hero.callButton}
              </a>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 animate-bounce hidden md:block">
          <div className="w-8 h-14 border-2 border-white/30 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-3 bg-white/60 rounded-full" />
          </div>
        </div>
      </div>

      {/* Features Section */}
      <section className="py-24 bg-green-950 dark:bg-gray-900 transition-colors duration-200">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            <div className="group">
              <div className="w-16 h-16 bg-green-900/50 backdrop-blur-sm rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <FontAwesomeIcon icon={faTree} className="w-8 h-8 text-green-400" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-white">{t.home.features.plants.title}</h3>
              <p className="text-gray-400">{t.home.features.plants.description}</p>
            </div>
            <div className="group">
              <div className="w-16 h-16 bg-green-900/50 backdrop-blur-sm rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Truck className="w-8 h-8 text-green-400" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-white">{t.home.features.transport.title}</h3>
              <p className="text-gray-400">{t.home.features.transport.description}</p>
            </div>
            <div className="group">
              <div className="w-16 h-16 bg-green-900/50 backdrop-blur-sm rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Shovel className="w-8 h-8 text-green-400" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-white">{t.home.features.design.title}</h3>
              <p className="text-gray-400">{t.home.features.design.description}</p>
            </div>
            <div className="group">
              <div className="w-16 h-16 bg-green-900/50 backdrop-blur-sm rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Phone className="w-8 h-8 text-green-400" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-white">{t.home.features.advice.title}</h3>
              <p className="text-gray-400">{t.home.features.advice.description}</p>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-24 bg-white dark:bg-gray-800 transition-colors duration-200">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="relative h-[480px] rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://grabczewscy.com/wp-content/uploads/2023/09/IMG_9452.jpg"
                alt={language === 'pl' ? 'Szkółka roślin - zieleń i drzewa' : 'Plant nursery - greenery and trees'}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-green-950/40 to-transparent" />
            </div>
            <div>
              <h2 className="text-4xl font-bold mb-8 text-green-950 dark:text-green-400 flex items-center">
                <FontAwesomeIcon icon={faTree} className="text-green-600 dark:text-green-400 h-8 w-8 mr-3" />
                {t.home.about.title}
              </h2>
              <div className="space-y-6 text-gray-700 dark:text-gray-300 leading-relaxed">
                {t.home.about.description.map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}